// frontend/src/utils/itemFieldMirror.js
//
// Item Setup renders one grid row per ItemLocation, so an item-level field
// (cost, sell price, default supplier, assigned user, ...) can appear on N rows
// for the same product. Editing one must show — and save from — the same value
// on every sibling row, because the save collapses those rows into ONE
// item-scoped write.
//
// The value mirrored to siblings (and recorded as the change on every row) is
// the EDITED ROW's stored field value. For a reference-picker column that value
// is the selected option OBJECT (e.g. `{ id, name, display_label }` for the
// category); the grid's own cell value is only the scalar id (the column's
// valueGetter returns `product_category.id`). Mirroring the scalar would
// overwrite a sibling's object with a bare id, so at save time
// `rowData.product_category?.id` reads `undefined` and the item's category is
// silently cleared. Reading the value off the edited row
// keeps whatever shape the column stores — object for pickers, scalar for plain
// columns — so the same helper is correct for every item-level field.

/**
 * Mirror an item-level cell edit across a product's loaded location rows.
 *
 * @param {object}   p
 * @param {object}   p.editedRow    rowData of the edited row; its field already
 *                                  holds the committed value / option object.
 * @param {string}   p.fieldName
 * @param {*}        p.oldValue     the edited cell's previous value.
 * @param {object[]} p.siblingRows  every OTHER loaded rowData (caller excludes
 *                                  the edited row).
 * @param {(entry: {row: object, fieldName: string, oldValue: *, newValue: *}) => void} p.record
 *                                  records one cell change (recordCellChange).
 * @returns {object[]} the sibling rows that were mirrored (same product, carries
 *   a location) — so the caller can refresh their grid nodes.
 */
export function mirrorItemFieldEdit({ editedRow, fieldName, oldValue, siblingRows, record }) {
  const value = editedRow[fieldName];
  record({ row: editedRow, fieldName, oldValue, newValue: value });

  const mirrored = [];
  for (const row of siblingRows) {
    if (!row || row._ItemID !== editedRow._ItemID || !row.item_location_id) continue;
    const previousValue = row[fieldName];
    row[fieldName] = value;
    record({ row, fieldName, oldValue: previousValue, newValue: value });
    mirrored.push(row);
  }
  return mirrored;
}
